import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

const Landing = () => {
  const navigate = useNavigate();
  const [featureIndex, setFeatureIndex] = useState(-1);
  const [visibleFeatures, setVisibleFeatures] = useState([]);
  const [userClosed, setUserClosed] = useState(false);
  const [paused, setPaused] = useState(false);

  const features = [
    {
      icon: "fas fa-shopping-bag",
      title: "Order Management",
      desc: "Create orders, search history, generate GST invoices & PDF bills instantly. Supports manual and stock-based billing with real-time price calculation.",
      color: "from-blue-500 to-indigo-600",
    },
    {
      icon: "fas fa-boxes",
      title: "Stock & Inventory",
      desc: "Real-time stock tracking with batch management, low-stock alerts, expiry notifications. Automatic stock deduction on order and restoration on return.",
      color: "from-green-500 to-teal-600",
    },
    {
      icon: "fas fa-undo-alt",
      title: "Return & Refund",
      desc: "Process partial or full returns with automatic stock restoration, profit reversal tracking, and refund recording. Complete financial audit trail.",
      color: "from-orange-500 to-red-500",
    },
    {
      icon: "fas fa-hand-holding-usd",
      title: "Payment Tracking",
      desc: "Track pending payments, collect partial payments, maintain payment history. Generate payment reports in PDF & Excel format.",
      color: "from-purple-500 to-pink-500",
    },
    {
      icon: "fas fa-chart-line",
      title: "Live Dashboard",
      desc: "Real-time dashboard showing today's sales, low stock alerts, expiry warnings, and pending payment dues. Auto-refreshing live data.",
      color: "from-cyan-500 to-blue-500",
    },
    {
      icon: "fas fa-users",
      title: "Customer Management",
      desc: "Customer profiles with mobile-based lookup, complete order history, payment tracking, and loyalty insights per customer.",
      color: "from-amber-500 to-orange-500",
    },
    {
      icon: "fas fa-truck",
      title: "Distributor & Purchase Bills",
      desc: "Manage distributors, create purchase bills, track distributor-wise investments and outstanding amounts.",
      color: "from-rose-500 to-pink-600",
    },
    {
      icon: "fas fa-tags",
      title: "Product Categories & HSN",
      desc: "Category-wise product classification with HSN codes, GST rates, and configurable discount rules per category.",
      color: "from-violet-500 to-purple-600",
    },
    {
      icon: "fas fa-file-invoice-dollar",
      title: "GST Billing & Invoices",
      desc: "Automatic GST calculation with CGST/SGST split, HSN-wise tax breakup. Download professional PDF invoices and Excel reports.",
      color: "from-emerald-500 to-green-600",
    },
  ];

  const startFeatureShow = useCallback(() => {
    setVisibleFeatures([]);
    setUserClosed(false);
    setPaused(false);
    setFeatureIndex(0);
  }, []);

  useEffect(() => {
    if (featureIndex < 0 || userClosed || paused) return;
    if (featureIndex >= features.length) return;

    const timer = setTimeout(
      () => {
        setVisibleFeatures((prev) => [...prev, features[featureIndex]]);
        setFeatureIndex((prev) => prev + 1);
      },
      featureIndex === 0 ? 0 : 2000,
    );

    return () => clearTimeout(timer);
  }, [featureIndex, userClosed, paused]);

  // Auto-remove each popup after 2 seconds
  useEffect(() => {
    if (visibleFeatures.length === 0 || userClosed || paused) return;

    const timer = setTimeout(() => {
      setVisibleFeatures((prev) => prev.slice(1));
    }, 2000);

    return () => clearTimeout(timer);
  }, [visibleFeatures, userClosed, paused]);

  const closeFeatures = () => {
    setFeatureIndex(-1);
    setVisibleFeatures([]);
    setUserClosed(true);
  };

  const removeFeature = (idx) => {
    setVisibleFeatures((prev) => prev.filter((_, i) => i !== idx));
    setUserClosed(true);
    setFeatureIndex(-1);
  };

  const plans = [
    {
      name: "Monthly",
      price: "₹999",
      duration: "/month",
      features: [
        "Full Access",
        "24/7 Support",
        "Cloud Backup",
        "Regular Updates",
      ],
      color: "from-blue-500 to-blue-600",
    },
    {
      name: "Quarterly",
      price: "₹2,499",
      duration: "/3 months",
      save: "Save 17%",
      features: [
        "All Monthly Features",
        "Priority Support",
        "Advanced Reports",
        "Free Training",
      ],
      popular: true,
      color: "from-purple-500 to-pink-500",
    },
    {
      name: "Half Yearly",
      price: "₹4,499",
      duration: "/6 months",
      save: "Save 25%",
      features: [
        "All Quarterly Features",
        "Dedicated Manager",
        "Custom Features",
        "API Access",
      ],
      color: "from-green-500 to-teal-500",
    },
    {
      name: "Yearly",
      price: "₹7,999",
      duration: "/year",
      save: "Save 33%",
      features: [
        "All Features Included",
        "VIP Support",
        "White Label Option",
        "Free Upgrades",
      ],
      color: "from-orange-500 to-red-500",
    },
  ];

  return (
    <div
      className={`min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-900 ${visibleFeatures.length > 0 ? "h-screen overflow-hidden" : "h-screen overflow-y-auto"}`}
    >
      {/* Navigation */}
      <nav className="bg-white/10 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl overflow-hidden shadow-lg border-2 border-yellow-400/50 bg-gradient-to-br from-yellow-50 to-white p-0.5">
              <img
                src="/logo.png"
                alt="JaShBill"
                className="w-full h-full object-contain rounded-lg"
              />
            </div>
            <h1 className="text-xl sm:text-2xl font-bold text-white">
              JaShBill
            </h1>
          </div>
          <div className="flex gap-2 sm:gap-4">
            <button
              onClick={() => navigate("/login")}
              className="px-4 sm:px-6 py-2 text-white font-semibold hover:bg-white/10 rounded-lg transition text-sm sm:text-base"
            >
              Login
            </button>
            <button
              onClick={() => navigate("/signup")}
              className="px-4 sm:px-6 py-2 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition text-sm sm:text-base"
            >
              Sign Up
            </button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
        <h3 className="text-3xl sm:text-3xl md:text-3xl font-extrabold text-white mb-6 leading-tight">
          Billing made simple.{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
            Business made powerful.
          </span>
        </h3>
        <p className="text-base sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto">
          Orders, Inventory, GST Billing, Returns, Payments & Analytics —
          everything your business needs in one powerful platform.
        </p>
        <div className="flex flex-wrap gap-4 justify-center">
          <button
            onClick={() => navigate("/signup")}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-yellow-400 to-orange-500 text-white font-bold text-sm sm:text-lg rounded-full shadow-2xl hover:shadow-yellow-500/50 transform hover:scale-105 transition"
          >
            <i className="fas fa-rocket mr-2" />
            Start Now
          </button>
          <button
            onClick={startFeatureShow}
            className="px-6 sm:px-8 py-3 sm:py-4 bg-white/10 backdrop-blur-md text-white font-bold text-sm sm:text-lg rounded-full border-2 border-white/30 hover:bg-white/20 transition"
          >
            <i className="fas fa-th-large mr-2" />
            Explore Features
          </button>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-16">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { num: "20+", label: "Shops" },
            { num: "1M+", label: "Orders Processed" },
            { num: "99.9%", label: "Uptime" },
            { num: "4.9★", label: "User Rating" },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center"
            >
              <p className="text-2xl sm:text-3xl font-extrabold text-yellow-400">
                {s.num}
              </p>
              <p className="text-xs sm:text-sm text-gray-300">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-white/5 backdrop-blur-md border border-white/20 rounded-3xl p-8 sm:p-12">
          <h3 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12">
            How It <span className="text-yellow-400">Works</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                icon: "fas fa-user-plus",
                title: "Sign Up",
                desc: "Create your account in 30 seconds",
              },
              {
                step: "2",
                icon: "fas fa-plus-circle",
                title: "Add Stock",
                desc: "Add your products & inventory",
              },
              {
                step: "3",
                icon: "fas fa-shopping-bag",
                title: "Create Orders",
                desc: "Bill customers with GST invoices",
              },
              {
                step: "4",
                icon: "fas fa-chart-pie",
                title: "Track & Grow",
                desc: "Monitor sales, profits & returns",
              },
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <i className={`${s.icon} text-white text-xl`} />
                </div>
                <span className="text-xs font-bold text-yellow-400 uppercase">
                  Step {s.step}
                </span>
                <h5 className="text-lg font-bold text-white mt-1 mb-1">
                  {s.title}
                </h5>
                <p className="text-gray-300 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h3 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
          Simple, <span className="text-yellow-400">Transparent Pricing</span>
        </h3>
        <p className="text-gray-300 text-center mb-12 text-sm sm:text-lg">
          Choose the plan that fits your business. No hidden fees.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan, i) => (
            <div
              key={i}
              className={`relative bg-white/10 backdrop-blur-md border-2 rounded-2xl p-6 hover:scale-105 transform transition duration-300 shadow-2xl ${
                plan.popular
                  ? "border-yellow-400 shadow-yellow-500/30"
                  : "border-white/20"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 rounded-full text-xs font-bold shadow-lg">
                    ⭐ POPULAR
                  </span>
                </div>
              )}
              <div className="text-center mb-6">
                <h4 className="text-xl font-bold text-white mb-2">
                  {plan.name}
                </h4>
                {plan.save && (
                  <span className="inline-block bg-green-500 text-white px-3 py-0.5 rounded-full text-xs font-semibold mb-2">
                    {plan.save}
                  </span>
                )}
                <div className="flex items-end justify-center gap-1">
                  <span className="text-3xl font-extrabold text-white">
                    {plan.price}
                  </span>
                  <span className="text-gray-300 text-sm mb-1">
                    {plan.duration}
                  </span>
                </div>
              </div>
              <ul className="space-y-2 mb-6">
                {plan.features.map((f, idx) => (
                  <li
                    key={idx}
                    className="flex items-center gap-2 text-gray-200 text-sm"
                  >
                    <i className="fas fa-check-circle text-green-400 text-xs" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate("/signup")}
                className={`w-full py-2.5 rounded-lg font-bold text-white text-sm bg-gradient-to-r ${plan.color} hover:shadow-xl transform hover:scale-105 transition`}
              >
                Get Started
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 rounded-3xl p-8 sm:p-12 text-center shadow-2xl">
          <h3 className="text-2xl sm:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Business?
          </h3>
          <p className="text-base sm:text-xl text-white/90 mb-8">
            Join hundreds of shops already using JaShBill
          </p>
          <button
            onClick={() => navigate("/signup")}
            className="px-8 sm:px-10 py-3 sm:py-4 bg-white text-orange-600 font-bold text-sm sm:text-lg rounded-full shadow-xl hover:shadow-2xl transform hover:scale-105 transition"
          >
            <i className="fas fa-arrow-right mr-2" />
            Start Now
          </button>
        </div>
      </section>

      {/*{/* Footer */}
      {/*<footer className="bg-black/30 backdrop-blur-md border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-7 h-7 rounded overflow-hidden bg-gradient-to-br from-white to-yellow-50 p-0.5 border border-yellow-400/40">
              <img
                src="/logo.png"
                alt="JaShBill"
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-white font-semibold">JaShBill</span>
          </div>
          <p className="text-gray-400 text-sm">
            © 2024 JaShBill. All rights reserved. | Complete Billing Solution
          </p>
          <div className="flex justify-center gap-6 mt-4">
            <span className="text-gray-400 hover:text-white transition cursor-pointer">
              <i className="fab fa-facebook text-lg" />
            </span>
            <span className="text-gray-400 hover:text-white transition cursor-pointer">
              <i className="fab fa-twitter text-lg" />
            </span>
            <span className="text-gray-400 hover:text-white transition cursor-pointer">
              <i className="fab fa-linkedin text-lg" />
            </span>
            <span className="text-gray-400 hover:text-white transition cursor-pointer">
              <i className="fab fa-instagram text-lg" />
            </span>
          </div>
        </div>
      </footer>*/}

      {/* Feature Popups — appear one by one */}
      {visibleFeatures.length > 0 && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 pointer-events-none">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto"
            onClick={closeFeatures}
          />
          <div className="relative w-full max-w-md space-y-4 max-h-[80vh] overflow-y-auto pointer-events-auto py-4">
            {visibleFeatures.map((f, i) => (
              <div
                key={i}
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
                className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-xl border border-white/30 rounded-2xl p-5 shadow-2xl animate-[slideUp_0.5s_ease-out]"
                style={{ animationFillMode: "both", animationDelay: "0s" }}
              >
                {/* Close button */}
                <button
                  onClick={() => removeFeature(i)}
                  className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center transition"
                >
                  <i className="fas fa-times text-white text-xs" />
                </button>
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 bg-gradient-to-br ${f.color} rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg`}
                  >
                    <i className={`${f.icon} text-white text-xl`} />
                  </div>
                  <div className="flex-1 pr-6">
                    <h4 className="text-base font-bold text-white mb-1">
                      {f.title}
                    </h4>
                    <p className="text-sm text-gray-300 leading-relaxed">
                      {f.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}

            {/* Close all button */}
            <div className="text-center pt-2">
              <button
                onClick={closeFeatures}
                className="px-6 py-2 bg-white/20 hover:bg-white/30 text-white font-semibold text-sm rounded-full border border-white/30 transition"
              >
                <i className="fas fa-times mr-2" />
                Close All
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <div className="fixed bottom-0 left-0 w-full bg-black/60 backdrop-blur-sm border-t border-white/10 py-2 text-white/70 text-xs z-50">
        {/* 🔹 Row 1: Contact */}
        <div className="flex justify-center gap-6">
          <a href="tel:+917827060095" className="hover:text-white">
            📞 +91 78270 60095
          </a>

          <a
            href="mailto:kumarjha.nand878@gmail.com?subject=Support%20Request"
            className="hover:text-white"
          >
            📧 kumarjha.nand878@gmail.com
          </a>
        </div>

        {/* 🔹 Row 2: Social + Copyright */}
        <div className="flex justify-center items-center gap-4 mt-1">
          <a
            href="https://facebook.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-blue-400"
          >
            <i className="fab fa-facebook"></i>
          </a>

          <a
            href="https://instagram.com"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-pink-400"
          >
            <i className="fab fa-instagram"></i>
          </a>

          <a
            href="https://wa.me/917827060095"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-green-400"
          >
            <i className="fab fa-whatsapp"></i>
          </a>

          <span className="text-white/40 mx-2">|</span>

          <span className="text-white/40 text-[10px]">
            © {new Date().getFullYear()} JashBill. All rights reserved.
          </span>
        </div>
      </div>
    </div>
  );
};

export default Landing;
